import {User, RequestResult, Employee, TableData} from '../shared/shared.model';

export interface Audit {
  id?: number;
  auditId?: string;
  auditTitle: string;
  auditCode: string;
  auditTypeId: number;
  auditType: string;
  auditDescription: string;
  auditOwnerGroup?: string;
  auditOwnerGroupId: number;
  dueDate: Date;
  externalPartner: string;
  status: string;
  smeApprovers: Array<string>;
  canSubmit?: boolean;
  capaActivityStatus?: string;
  findingCount?: number;
  draftFindingCount?: number;
  capaCount?: number;
  canUpdate?: boolean;
  submiters: Array<string>;
  smeApprover: string;
  submiter: string;
  history :string;
}

export interface AuditHistory {
  id?: number;
  auditId?: string;
  auditTitle: string;
  auditCode: string;
  auditTypeId: number;
  auditType: string;
  auditDescription: string;
  auditOwnerGroup?: string;
  auditOwnerGroupId: number;
  dueDate: Date;
  externalPartner: string;
  status: string;
  smeApprovers: Array<string>;
  canSubmit?: boolean;
  capaActivityStatus?: string;
  findingCount?: number;
  draftFindingCount?: number;
  capaCount?: number;
  canUpdate?: boolean;
  submiters: Array<string>;
  smeApprover: string;
  submiter: string;
  createdBy : string;
  createdOn : Date;
  updatedBy : string;
  updatedOn : Date;
}

export interface Finding {
  id?: number;
  auditId?: number;
  auditTypeId?: number;
  auditCode?: string;
  findingId?: string;
  findingCode?: string;
  findingItem?: string;
  findingCategory?: string;
  findingTitle?: string;
  findingDescription?: string;
  findingOwner?: Array<FindingOwner>;
  extPartner?: string;
  extService?: string;
  applicability?: string;
  rootCause?: string;
  impact?: string;
  registrationDate?: Date;
  dueDate?: Date;
  completionDate?: Date;
  submitter?: string;
  status?: string;
  capaCount?: number;
  draftCapaCount?: number;
  capaActivityStatus?: string;
  auditDueDate?: Date;
  
}

export interface Capa {
  id?: number;
  capaId?: string;
  sidId?: number;
  findingDataId?: number;
  capaTitle?: string;
  capaEcType?: string;
  evidenceDueDate?: Date;
  description?: string;
  allMustSubmit?: boolean;
  capaPics?: Array<CapaPic>;
  status?: string;
  canSubmitEvidence?: boolean;
  canApprove?: boolean;
  canUpdate?: boolean;
  history : string;
  updateOn?:string;
  submiter?:string;
  smeApprover?:string;
}

export interface ActivityHistory {
  id?: number;
  capaId?: string;
  name?: string;
  status?: string;
  dueDate?: Date;
  submissionDate?: Date;
  createdBy?: string;
  createdOn?: Date;
}

export interface AuditFilter {
  auditTypeId?: Array<number>;
  status?: Array<string>;
  dateType?: string;
  date?: Date;
  sme?: Array<number>;
  ownerId?: Array<number>;
  external?: string;
  capaInProgress?: boolean;
  capaOverdue?: boolean;
  capaCompleted?: boolean;
}

export interface SidFilter {
  applicabilityId?: Array<number>;
  status?: Array<string>;
  dateType?: string;
  date?: Date;
  sme?: Array<string>;
  ownerId?: Array<string>;
  external?: string;
  deviationOwner?: Array<string>;
  capaInProgress?: boolean;
  capaOverdue?: boolean;
  capaCompleted?: boolean;
}

export interface Evidence {
  id?: number;
  evidenceId?: string;
  fileName?: string;
  description?: string;
  capaId?: number;
  uploadedBy?: string;
  uploadedOn?: Date;
  describedBy?: string;
  describedOn?: Date;
  status?: string;
  reason?: string;
  createdBy?: string;
  createdOn?: Date;
  modifiedBy?: string;
  modifiedOn?: Date;
  canSubmitEvidence?: boolean;
  history? :string;
}

export interface TableDataEvidence<T> extends TableData<T> {
  canAddEvidence?: boolean;
  hasHistory?:boolean;
}

export interface EvidenceApproval {
  evidenceId: number;
  status: string;
  reason?: string;
}

export interface CreateUpdateEvidence {
  id?: number;
  description?: string;
  file?: File;
}

export interface EvidenceApprovalResult extends RequestResult {
  resultStatus?: string;
}

export class AuditUtils {
  static sanitize(audit: Audit): Audit {
    return {
      id: audit.id,
      smeApprovers: audit.smeApprovers,
      auditTitle: audit.auditTitle,
      auditCode: audit.auditCode,
      auditType: audit.auditType,
      auditTypeId: audit.auditTypeId,
      auditDescription: audit.auditDescription,
      dueDate: audit.dueDate,
      auditOwnerGroupId: audit.auditOwnerGroupId,
      status: audit.status,
      externalPartner: audit.externalPartner,
      submiters:audit.submiters,
      smeApprover : audit.smeApprover,
      submiter : audit.submiter,
      history : audit.history,
    };
  }
}

export interface AuditBase {
  id?: number;
  isActive: boolean;
}

export interface AuditType extends AuditBase {
  auditType: string;
}

export interface AuditOwnerGroup extends AuditBase {
  auditOwnerGroup: string;
  smes: Array<User>;
  admins: Array<User>
}

export interface SMEApprover extends User {}
export interface FindingOwner extends Employee {}
export interface CapaPic extends User {}

export enum CapaActivityStatus {
  DRAFT = 'draft',
  OVERDUE = 'Overdue',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
}

export enum CapaStatus {
  DRAFT = 'draft',
  READY_TO_SUBMIT = 'Ready to Submit',
  PENDING_FOR_EVIDENCE = 'Pending for Evidence',
  PENDING_APPROVAL = 'Pending Approval',
  APPROVED = 'Approved',
  RETURNED = 'Returned',
}

export enum FindingStatus {
  DRAFT = 'draft',
  READY_TO_SUBMIT = 'Ready to Submit',
  PENDING = 'Pending',
  COMPLETED = 'Completed',
}

export enum EvidenceStatus {
  RETURN = 'Returned',
  APPROVED = 'Approved',
}

export enum DateType {
  DUE_DATE = 'Due Date',
  COMPLETION_DATE = 'Completion Date',
  REGISTRATION_DATE = 'Registration Date',
}

export enum AuditStatus {
  COMPLETED = 'completed',
  DRAFT = 'draft',
  PENDING = 'pending',
}

export const CapaECTypes = [
  {
    key: 'CA',
    text: 'Corrective Action',
  },
  {
    key: 'PA',
    text: 'Preventive Action',
  },
  {
    key: 'AI',
    text: 'Action Items',
  },
  {
    key: 'EC',
    text: 'Effectiveness Check',
  },
];